import React, { useEffect, useState } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography, Box } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { URL_PREFIX } from '../../constants/stores';

interface FileViewProps {
  id: string;
  fetchApi: string;
  deleteApi: string;
  files: string[];
}

const FileView: React.FC<FileViewProps> = ({ id, fetchApi, deleteApi, files }) => {
  const token = useSelector((state: any) => state.user.sessionId);
  const [fileList, setFileList] = useState<string[]>(files || []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setFileList(files || []);
  }, [files]);

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteFile = async (file: string) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${file}?`);
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${URL_PREFIX}${deleteApi}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, file }),
      });

      const { data } = await response.json();

      if (response.status === 200) {
        setFileList(data.images || []);
        alert('Deleted successfully.');
      } else {
        alert('Unable to delete the file.');
      }
    } catch (error) {
      console.log('Error deleting:', error);
      alert('Something went wrong.');
    }
  };

  const handleOpenFile = async (file: string) => {
    try {
      const response = await fetch(`${fetchApi}${file}`);
      if (!response.ok) {
        alert('File not found');
        throw new Error('File not found');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Tooltip title={fileList.length ? `View ${fileList.length} file(s)` : 'No files'}>
        <span>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            disabled={fileList.length === 0}
            sx={{ p: 0.5 }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Typography variant="caption" sx={{ minWidth: 14 }}>
        {fileList.length}
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {fileList.length === 0 ? (
          <MenuItem disabled>No files</MenuItem>
        ) : (
          fileList.map((file) => (
            <MenuItem
              key={file}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                minWidth: 240,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={file}
              >
                {file}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title="Open file">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenFile(file);
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete file">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFile(file);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default FileView;